import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCarOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { colors } from '../../const/colors'
import { IPlateRow } from './useParkingUserDetails'
import { SectionActions, SectionButton } from '../Shared/SectionButton'
import { CARD_SX, SectionHeader } from './UserDetailsUi'

const CarPlatesSection: FC<{
  plates: IPlateRow[]
  canEditPlates: boolean
  platesDirty: boolean
  onUpdatePlate: (id: string, value: string) => void
  onRemovePlate: (id: string) => void
  onAddPlate: () => void
  onSave: () => void
  step?: number
  stepDone?: boolean
}> = ({
  plates,
  canEditPlates,
  platesDirty,
  onUpdatePlate,
  onRemovePlate,
  onAddPlate,
  onSave,
  step,
  stepDone,
}) => {
  const { t } = useTranslation(['user_details', 'tenant', 'btn'])

  return (
    <Box sx={CARD_SX} data-e2e="car-plates-section">
      <SectionHeader
        icon={<DirectionsCarIcon sx={{ fontSize: 18 }} />}
        title={t('car_plates')}
        step={step}
        done={stepDone}
      />
      {!canEditPlates && (
        <Typography
          data-e2e="car-plates-info"
          sx={{
            fontSize: '13px',
            color: colors.textMuted,
            mb: '10px',
            lineHeight: 1.5,
          }}
        >
          {t('parking_pool:no_car_plate_assigned_info')}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
        {plates.map(({ id, value, error }) => {
          return (
            <Box key={id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                <Box
                  component="input"
                  data-e2e="car-plate-input"
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onUpdatePlate(id, e.target.value)
                  }
                  placeholder={t('car_plate_placeholder')}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 48,
                    px: '15px',
                    border: `1.5px solid ${error ? colors.red : colors.border3}`,
                    borderRadius: '12px',
                    fontSize: '14.5px',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    color: colors.navy,
                    background: '#fff',
                    fontFamily: 'monospace',
                    outline: 'none',
                    textTransform: 'uppercase',
                    '&:focus': {
                      borderColor: error ? colors.red : colors.navy,
                    },
                  }}
                />
                <SectionButton
                  label={t('remove_car_plate')}
                  e2e="remove-car-plate"
                  icon={<DeleteOutlineIcon sx={{ fontSize: 17 }} />}
                  tone="danger"
                  height={48}
                  onClick={() => onRemovePlate(id)}
                />
              </Box>
              {error && (
                <Typography
                  data-e2e="car-plate-error"
                  sx={{
                    fontSize: '12.5px',
                    color: colors.red,
                    mt: '5px',
                    pl: '4px',
                  }}
                >
                  {error}
                </Typography>
              )}
            </Box>
          )
        })}
        {plates.length === 0 && (
          <Typography
            sx={{ fontSize: '13px', color: colors.slate, p: '2px 2px 4px' }}
          >
            {t('no_car_plates')}
          </Typography>
        )}
        {plates.length < 2 && canEditPlates && (
          <Box
            onClick={onAddPlate}
            data-e2e="add-car-plate"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              height: 46,
              border: `1.5px dashed ${colors.borderDash}`,
              borderRadius: '12px',
              color: colors.textSecondary,
              fontWeight: 700,
              fontSize: '13.5px',
              cursor: 'pointer',
              transition: 'border-color .15s, color .15s',
              '&:hover': { borderColor: colors.navy, color: colors.navy },
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            {plates.length ? t('add_next_car_plate') : t('add_car_plate')}
          </Box>
        )}
      </Box>
      {canEditPlates && (
        <SectionActions>
          <SectionButton
            label={t('btn:save')}
            icon={<CheckIcon sx={{ fontSize: 17 }} />}
            disabled={!platesDirty}
            e2e="save-car-plates"
            onClick={onSave}
          />
        </SectionActions>
      )}
    </Box>
  )
}

export default CarPlatesSection
